import { OPENAI_API_KEY } from '@env';

// AI Import Service for parsing website content into opportunities
export interface ParsedOpportunity {
  title: string;
  description: string;
  type: string | string[]; // Can be single string or array for multi-select
  organization: string;
  location: string;
  website?: string;
  eligibility?: string;
  cost?: string;
  tags?: string[];
  gradeLevel?: string[];
  interactionTypes?: string[];
  startDate?: string;
  endDate?: string;
  applicationOpenDate?: string;
  applicationCloseDate?: string;
  isYearlyRecurring?: boolean;
}

interface AIImportResult {
  opportunities: ParsedOpportunity[];
  confidence: number;
  processingTime: number;
}

let openaiClient: any = null;

// Initialize OpenAI client
const getOpenAIClient = async () => {
  if (openaiClient) return openaiClient;
  
  try {
    const { default: OpenAI } = await import('openai');
    
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key not configured');
    }
    
    openaiClient = new OpenAI({
      apiKey: OPENAI_API_KEY,
      dangerouslyAllowBrowser: true
    });
    
    return openaiClient;
  } catch (error) {
    console.error('🤖 Failed to initialize OpenAI for AI Import:', error);
    throw error;
  }
};

// Parse website content using GPT-4o mini
export const parseWebsiteContent = async (content: string, organizationName?: string, importType: 'single' | 'multiple' = 'multiple'): Promise<AIImportResult> => {
  const startTime = Date.now();
  
  try {
    console.log('🤖 Starting AI content parsing...');
    console.log('📄 Content length:', content.length);
    
    const client = await getOpenAIClient();
    
    // Create a tailored prompt based on import type
    const isSingleImport = importType === 'single';
    
    const prompt = `You are an expert at extracting educational and professional opportunities from website content. 

${isSingleImport 
  ? `Analyze the following website content and extract the MAIN or PRIMARY opportunity (program, competition, grant, scholarship, workshop, conference, etc.) that is most prominently featured or described.

Focus on finding the single most important opportunity that the content is primarily about. If multiple opportunities are mentioned, choose the one that is most detailed or prominently featured.`
  : `Analyze the following website content and extract ALL opportunities (programs, competitions, grants, scholarships, workshops, conferences, etc.) that you can identify.

Look for every distinct opportunity mentioned in the content, even if some information is missing.`
}

For each opportunity, provide the following information in a structured JSON format:

{
  "opportunities": [
    {
      "title": "Clear, descriptive title of the opportunity",
      "description": "Detailed description of what participants will do, learn, or achieve",
      "type": ["Competition", "Grant", "Scholarship", "Hackathon", "Internship", "Workshop/Programs", "Conference", "Other"],
      "organization": "Will be set to the actual organization name automatically",
      "location": "City, Province/State, Country or 'Online' or 'Virtual'",
      "website": "Full URL if available",
      "eligibility": "Who can apply (age, grade level, requirements, etc.)",
      "cost": "Cost information (Free, $X, Scholarships available, etc.)",
      "tags": ["relevant", "keywords", "for", "search"],
      "gradeLevel": ["Elementary", "Middle School", "High School", "Undergraduate", "Graduate", "Professional", "All Levels"],
      "interactionTypes": ["In-Person", "Virtual", "Hybrid"],
      "startDate": "MM/DD/YYYY format if available",
      "endDate": "MM/DD/YYYY format if available", 
      "applicationOpenDate": "MM/DD/YYYY format if available",
      "applicationCloseDate": "MM/DD/YYYY format if available",
      "isYearlyRecurring": true/false
    }
  ],
  "confidence": 0.85
}

IMPORTANT GUIDELINES:
${isSingleImport 
  ? `1. Extract ONLY the main/primary opportunity from the content
2. If multiple opportunities exist, choose the most detailed or prominent one
3. Provide comprehensive details for the single opportunity`
  : `1. Extract ALL opportunities you can find, even if some information is missing
2. Each opportunity should be a separate entry in the array`
}
3. Be conservative with dates - only include them if clearly stated
4. For missing information, use reasonable defaults or leave empty
5. Focus on educational, professional development, and career opportunities
6. Include both current and upcoming opportunities
7. If no opportunities are found, return empty array with low confidence
8. Set confidence based on how clear and complete the information is
9. For the "type" field, select ALL applicable types from the array (can be multiple)
10. For "gradeLevel" and "interactionTypes", select ALL applicable options from their respective arrays
11. The "organization" field will be automatically set to the actual organization name - do not try to extract it from content

Website content to analyze:
${content.substring(0, 8000)}`; // Limit content to avoid token limits

    const response = await client.chat.completions.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an expert at extracting structured opportunity data from website content. Always respond with valid JSON.'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      temperature: 0.2, // Low temperature for consistent extraction
    });

    const responseText = response.choices[0]?.message?.content;
    if (!responseText) {
      throw new Error('No response from AI model');
    }

    console.log('🤖 AI Response received, length:', responseText.length);
    
    // Parse the JSON response
    let parsedResult: AIImportResult;
    try {
      // Clean the response to extract JSON
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in response');
      }
      
      parsedResult = JSON.parse(jsonMatch[0]);
    } catch (parseError) {
      console.error('❌ Failed to parse AI response as JSON:', parseError);
      console.log('Raw response:', responseText);
      throw new Error('Failed to parse AI response');
    }

    // Validate and clean the results
    if (!parsedResult.opportunities || !Array.isArray(parsedResult.opportunities)) {
      throw new Error('Invalid response format: opportunities array missing');
    }

    // Clean and validate each opportunity
    const cleanedOpportunities = parsedResult.opportunities.map((opp: any, index: number) => {
      return {
        title: String(opp.title || `Opportunity ${index + 1}`).trim(),
        description: String(opp.description || '').trim(),
        type: String(opp.type || 'Other').trim(),
        organization: String(organizationName || 'Unknown Organization').trim(), // Always use the actual organization name
        location: String(opp.location || 'Location TBD').trim(),
        website: opp.website ? String(opp.website).trim() : undefined,
        eligibility: opp.eligibility ? String(opp.eligibility).trim() : undefined,
        cost: opp.cost ? String(opp.cost).trim() : undefined,
        tags: Array.isArray(opp.tags) ? opp.tags.map((tag: any) => String(tag).trim()).filter(Boolean) : [],
        gradeLevel: Array.isArray(opp.gradeLevel) ? opp.gradeLevel.filter((level: any) => 
          ['Elementary', 'Middle School', 'High School', 'Undergraduate', 'Graduate', 'Professional', 'All Levels'].includes(String(level))
        ) : [],
        interactionTypes: Array.isArray(opp.interactionTypes) ? opp.interactionTypes.filter((type: any) =>
          ['In-Person', 'Virtual', 'Hybrid'].includes(String(type))
        ) : [],
        startDate: opp.startDate ? String(opp.startDate).trim() : undefined,
        endDate: opp.endDate ? String(opp.endDate).trim() : undefined,
        applicationOpenDate: opp.applicationOpenDate ? String(opp.applicationOpenDate).trim() : undefined,
        applicationCloseDate: opp.applicationCloseDate ? String(opp.applicationCloseDate).trim() : undefined,
        isYearlyRecurring: Boolean(opp.isYearlyRecurring)
      };
    }).filter(opp => opp.title && opp.description); // Filter out invalid opportunities

    const processingTime = Date.now() - startTime;
    
    const result: AIImportResult = {
      opportunities: cleanedOpportunities,
      confidence: Math.max(0, Math.min(1, parsedResult.confidence || 0.5)),
      processingTime
    };

    console.log('✅ AI parsing completed successfully');
    console.log(`📊 Found ${result.opportunities.length} opportunities`);
    console.log(`⏱️ Processing time: ${processingTime}ms`);
    console.log(`🎯 Confidence: ${(result.confidence * 100).toFixed(1)}%`);

    return result;

  } catch (error) {
    const processingTime = Date.now() - startTime;
    console.error('❌ AI content parsing failed:', error);
    
    // Return empty result with error info
    return {
      opportunities: [],
      confidence: 0,
      processingTime
    };
  }
};

// Validate and clean opportunity data before saving
export const validateOpportunityData = (opportunity: ParsedOpportunity): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!opportunity.title?.trim()) {
    errors.push('Title is required');
  }
  
  if (!opportunity.description?.trim()) {
    errors.push('Description is required');
  }
  
  if (!opportunity.organization?.trim()) {
    errors.push('Organization is required');
  }
  
  if (!opportunity.location?.trim()) {
    errors.push('Location is required');
  }
  
  // Validate opportunity type
  const validTypes = ['Competition', 'Grant', 'Scholarship', 'Hackathon', 'Internship', 'Workshop/Programs', 'Conference', 'Other'];
  const types = Array.isArray(opportunity.type) ? opportunity.type : [opportunity.type];
  if (types.length === 0 || !types[0]?.trim()) {
    errors.push('At least one opportunity type is required');
  } else {
    const invalidTypes = types.filter(type => !validTypes.includes(type));
    if (invalidTypes.length > 0) {
      errors.push(`Invalid opportunity types: ${invalidTypes.join(', ')}`);
    }
  }
  
  // Validate dates if provided
  const dateFields = ['startDate', 'endDate', 'applicationOpenDate', 'applicationCloseDate'];
  dateFields.forEach(field => {
    const dateValue = opportunity[field as keyof ParsedOpportunity] as string;
    if (dateValue && !isValidDate(dateValue)) {
      errors.push(`Invalid ${field} format`);
    }
  });
  
  return {
    valid: errors.length === 0,
    errors
  };
};

// Helper function to validate date format
const isValidDate = (dateString: string): boolean => {
  if (!dateString) return false;
  
  // Check MM/DD/YYYY format
  const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
  if (!dateRegex.test(dateString)) return false;
  
  // Check if it's a valid date
  const [month, day, year] = dateString.split('/').map(Number);
  const date = new Date(year, month - 1, day);
  
  return date.getFullYear() === year && 
         date.getMonth() === month - 1 && 
         date.getDate() === day;
};

// Convert parsed opportunity to database format
export const convertToDatabaseFormat = (opportunity: ParsedOpportunity, organizationUserId: string) => {
  const convertDate = (dateString?: string): Date | undefined => {
    if (!dateString || !isValidDate(dateString)) return undefined;
    const [month, day, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  return {
    title: opportunity.title.trim(),
    description: opportunity.description.trim(),
    type: Array.isArray(opportunity.type) ? opportunity.type.join(", ") : opportunity.type,
    organization: opportunity.organization.trim(),
    location: opportunity.location.trim(),
    website: opportunity.website?.trim(),
    eligibility: opportunity.eligibility?.trim(),
    cost: opportunity.cost?.trim(),
    tags: opportunity.tags || [],
    gradeLevel: opportunity.gradeLevel || [],
    interactionTypes: opportunity.interactionTypes || [],
    startDate: convertDate(opportunity.startDate),
    endDate: convertDate(opportunity.endDate),
    applicationOpenDate: convertDate(opportunity.applicationOpenDate),
    applicationCloseDate: convertDate(opportunity.applicationCloseDate),
    isYearlyRecurring: opportunity.isYearlyRecurring || false,
    isClaimed: true, // AI-imported opportunities are automatically claimed
    createdBy: organizationUserId,
    status: 'Open' // Default status
  };
};
